import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { DeviceWithKeyInfo, AnyDeviceType, BuildingType } from 'commons/src/models/commonTypeScript';
import { setInsightSelectedDevices } from '../../../actions/insightActions';
import { fetchBuilding } from '../../../actions/locationActions';
import { useGenerateWellReportMutation } from '../../../api/wellReportApi';
import { Store } from '../../../reducers';
import { BusinessRequestType } from '../../../reducers/BusinessRequestType';
import { validateBuildingData } from '../insightFunctions';

type UseWellFormProps = {
    acceptedDeviceTypes: AnyDeviceType[];
    possibleSensorTypes: SensorTypes[];
    optionalBuildingProps: string[];
};

const useWellForm = ({
    acceptedDeviceTypes,
    possibleSensorTypes,
    optionalBuildingProps,
}: UseWellFormProps): {
    building: BuildingType | undefined;
    selectedDevices: DeviceWithKeyInfo[];
    reportStartTime: Moment | null;
    reportEndTime: Moment | null;
    displayValidation: boolean;
    errorMessage: string | null;
    isFormValid: boolean;
    hasShownSuccess: boolean;
    fetchBuildingLoading: boolean;
    fetchReportDataLoading: boolean;
    earliestSegmentStartDate: string | undefined;
    earliestSegmentEndDate: string | undefined;
    locationOptions: Array<{ id: string; inputValue: string }>;
    buildingName: string | null;
    setReportStartTime: (value: Moment | null) => void;
    setReportEndTime: (value: Moment | null) => void;
    setDatesAreValid: (value: boolean) => void;
    selectBuildingFromDropdown: (selected: { id: string; inputValue: string }) => void;
    updateDeviceSelection: (selectedDevicesList: DeviceWithKeyInfo[]) => void;
    generateReport: () => Promise<void>;
} => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const [generateWellReport, { isLoading: fetchReportDataLoading, isSuccess }] = useGenerateWellReportMutation();

    const locations = useSelector((state: Store) => state.locations.locations);
    const buildings = useSelector((state: Store) => state.buildings.buildings);
    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);
    const selectedDevices = useSelector((state: Store) => state.radonInsight.selectedDevices);
    const fetchBuildingLoading = useSelector(
        (state: Store) => state.requests[BusinessRequestType.FetchBuilding]?.loading || false
    );

    const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
    const [reportStartTime, setReportStartTime] = useState<Moment | null>(null);
    const [reportEndTime, setReportEndTime] = useState<Moment | null>(null);
    const [selectedSensors, setSelectedSensors] = useState<SensorTypes[]>([]);
    const [datesAreValid, setDatesAreValid] = useState(false);
    const [displayValidation, setDisplayValidation] = useState(false);
    const [invalidElements, setInvalidElements] = useState<string[]>([]);
    const [hasShownSuccess, setHasShownSuccess] = useState(false);
    const hasProcessedSuccessRef = useRef(false);

    const building = useMemo(
        () => (selectedBuildingId ? buildings[selectedBuildingId] : undefined),
        [selectedBuildingId, buildings]
    );

    const resetForm = useCallback((): void => {
        setSelectedBuildingId('');
        setReportStartTime(null);
        setReportEndTime(null);
        setSelectedSensors([]);
        setDatesAreValid(false);
        setDisplayValidation(false);
        setInvalidElements([]);
        dispatch(setInsightSelectedDevices([]));
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && !hasProcessedSuccessRef.current) {
            dispatch(displayAlertBoxSagaAction('WellReport.EmailSentSuccess', false, true));
            resetForm();
            setHasShownSuccess(true);
            hasProcessedSuccessRef.current = true;
        }
    }, [isSuccess, dispatch, resetForm]);

    const hasFormData = useMemo(
        () => !!(selectedBuildingId || reportStartTime || reportEndTime || selectedDevices.length > 0),
        [selectedBuildingId, reportStartTime, reportEndTime, selectedDevices.length]
    );

    useEffect(() => {
        if (hasShownSuccess && hasFormData) {
            setHasShownSuccess(false);
            hasProcessedSuccessRef.current = false;
        }
    }, [hasShownSuccess, hasFormData]);

    const selectBuilding = useCallback(
        (locationId: string): void => {
            setSelectedBuildingId(locationId);
            dispatch(setInsightSelectedDevices([]));
            setSelectedSensors([]);
            if (locationId) {
                dispatch(fetchBuilding(locationId));
            }
        },
        [dispatch]
    );

    const updateDeviceSelection = useCallback(
        (selectedDevicesList: DeviceWithKeyInfo[]): void => {
            dispatch(setInsightSelectedDevices(selectedDevicesList));
            if (selectedDevicesList.length > 0 && !selectedSensors.length) {
                setSelectedSensors(possibleSensorTypes);
            }
        },
        [dispatch, selectedSensors.length, possibleSensorTypes]
    );

    const validateReport = useCallback((): { missingBuildingElements: string[] } => {
        if (!building) {
            return { missingBuildingElements: [] };
        }
        const invalidBuildingElement = validateBuildingData({ txt }, building, optionalBuildingProps);
        return { missingBuildingElements: invalidBuildingElement };
    }, [building, txt, optionalBuildingProps]);

    const isFormValid = useMemo(
        (): boolean =>
            !!building && datesAreValid && !!reportStartTime && !!reportEndTime && selectedDevices.length > 0,
        [building, datesAreValid, reportStartTime, reportEndTime, selectedDevices.length]
    );

    const generateReport = useCallback(async (): Promise<void> => {
        if (!building) {
            setInvalidElements(['Building']);
            setDisplayValidation(true);
            return;
        }

        const { missingBuildingElements } = validateReport();
        const reportRangeValid = datesAreValid && reportStartTime && reportEndTime;

        if (reportRangeValid && missingBuildingElements.length === 0 && selectedDevices.length > 0) {
            const fromDate = reportStartTime.format('YYYY-MM-DD');
            const toDate = reportEndTime.format('YYYY-MM-DD');
            const deviceIds = selectedDevices.map(device => parseInt(device.serialNumber, 10));

            try {
                await generateWellReport({
                    locationId: building.id,
                    from: fromDate,
                    to: toDate,
                    devices: deviceIds,
                }).unwrap();
            } catch {
                dispatch(displayAlertBoxSagaAction('WellReport.GenerationError', true, true));
            }
        } else {
            setDisplayValidation(true);
            const errors: string[] = [];
            if (!reportRangeValid) errors.push('Time period');
            if (missingBuildingElements.length > 0) errors.push(...missingBuildingElements);
            if (selectedDevices.length === 0) errors.push('Devices');
            setInvalidElements(errors);
        }
    }, [
        building,
        validateReport,
        datesAreValid,
        reportStartTime,
        reportEndTime,
        selectedDevices,
        generateWellReport,
        dispatch,
    ]);

    const allAcceptedDevicesForDates = useMemo(() => {
        if (!building) return [];
        return building.devices
            .map(serialNumber => devices[serialNumber])
            .filter(
                (device): device is DeviceWithKeyInfo =>
                    device !== undefined && acceptedDeviceTypes.includes(device.type)
            );
    }, [building, devices, acceptedDeviceTypes]);

    const devicesForDateCalculation = useMemo(
        () => (selectedDevices.length > 0 ? selectedDevices : allAcceptedDevicesForDates),
        [selectedDevices, allAcceptedDevicesForDates]
    );

    const dateRange = useMemo((): { startDate?: string; endDate?: string } => {
        if (devicesForDateCalculation.length === 0) {
            return { startDate: undefined, endDate: undefined };
        }

        return devicesForDateCalculation.reduce(
            (range, device) => {
                const startDate =
                    !range.startDate || moment(device.segmentStarted).isBefore(range.startDate)
                        ? device.segmentStarted
                        : range.startDate;
                const endDate =
                    !range.endDate || moment(device.segmentEnded).isBefore(range.endDate)
                        ? device.segmentEnded
                        : range.endDate;
                return { startDate, endDate };
            },
            { startDate: undefined as string | undefined, endDate: undefined as string | undefined }
        );
    }, [devicesForDateCalculation]);

    const earliestSegmentStartDate = dateRange.startDate;
    const earliestSegmentEndDate = dateRange.endDate;

    const locationOptions = useMemo(
        () => locations.map(location => ({ id: location.id, inputValue: location.name })),
        [locations]
    );

    const buildingName = useMemo(() => building?.name ?? null, [building]);

    const selectBuildingFromDropdown = useCallback(
        (selected: { id: string; inputValue: string }): void => {
            selectBuilding(selected.id);
        },
        [selectBuilding]
    );

    const errorMessage = useMemo(
        () =>
            displayValidation && invalidElements.length > 0
                ? txt('RadonInsight.FillOutMissingReportInfo', {
                      invalidElements: invalidElements.join(', '),
                  })
                : null,
        [displayValidation, invalidElements, txt]
    );

    return {
        building,
        selectedDevices,
        reportStartTime,
        reportEndTime,
        displayValidation,
        errorMessage,
        isFormValid,
        hasShownSuccess,
        fetchBuildingLoading,
        fetchReportDataLoading,
        earliestSegmentStartDate,
        earliestSegmentEndDate,
        locationOptions,
        buildingName,
        setReportStartTime,
        setReportEndTime,
        setDatesAreValid,
        selectBuildingFromDropdown,
        updateDeviceSelection,
        generateReport,
    };
};

export default useWellForm;
